import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import infrastructureMonitoringService from '../../../services/infrastructureMonitoringService';

const ClientResourcePanel = ({ timeRange }) => {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadClientResources();
  }, [timeRange, filter]);

  const loadClientResources = async () => {
    try {
      setLoading(true);
      const filters = { timeRange, limit: 50 };
      if (filter !== 'all') {
        filters.alertLevel = filter;
      }
      const data = await infrastructureMonitoringService?.getClientResourceUtilization(filters);
      setResources(data);
    } catch (error) {
      console.error('Error loading client resources:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAlertColor = (level) => {
    const colors = {
      normal: 'text-green-600 bg-green-50',
      warning: 'text-yellow-600 bg-yellow-50',
      critical: 'text-red-600 bg-red-50'
    };
    return colors?.[level] || 'text-gray-600 bg-gray-50';
  };

  const getUsageColor = (percentage) => {
    if (percentage < 70) return 'bg-green-600';
    if (percentage < 85) return 'bg-yellow-600';
    return 'bg-red-600';
  };

  return (
    <div>
      {/* Filter Controls */}
      <div className="mb-6 flex items-center gap-4">
        <span className="text-sm font-medium text-gray-700">Filter by Alert Level:</span>
        <div className="flex gap-2">
          {['all', 'normal', 'warning', 'critical']?.map((level) => (
            <button
              key={level}
              onClick={() => setFilter(level)}
              className={`
                px-4 py-2 text-sm font-medium rounded-md transition-colors
                ${filter === level
                  ? 'bg-blue-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }
              `}
            >
              {level?.charAt(0)?.toUpperCase() + level?.slice(1)}
            </button>
          ))}
        </div>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Icon name="Loader" className="w-6 h-6 text-blue-600 animate-spin" />
        </div>
      ) : resources?.length === 0 ? (
        <div className="text-center py-12">
          <Icon name="Server" className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">No client resource data available</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  CPU Usage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Memory Usage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Storage Usage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Connections
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  API Requests
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Alert Level
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Recorded At
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {resources?.map((resource) => (
                <tr key={resource?.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{resource?.companyName}</div>
                    {resource?.loadBalancingRecommendation && (
                      <div className="text-xs text-gray-500 mt-1">{resource?.loadBalancingRecommendation}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2 w-20">
                        <div
                          className={`h-2 rounded-full ${getUsageColor(resource?.cpuUsagePercentage)}`}
                          style={{ width: `${resource?.cpuUsagePercentage}%` }}
                        />
                      </div>
                      <span className="text-sm text-gray-700">{resource?.cpuUsagePercentage}%</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2 w-20">
                        <div
                          className={`h-2 rounded-full ${getUsageColor(resource?.memoryUsagePercentage)}`}
                          style={{ width: `${resource?.memoryUsagePercentage}%` }}
                        />
                      </div>
                      <span className="text-sm text-gray-700">{resource?.memoryUsagePercentage}%</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{resource?.memoryUsageMb} MB</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2 w-20">
                        <div
                          className={`h-2 rounded-full ${getUsageColor(resource?.storageUsagePercentage)}`}
                          style={{ width: `${resource?.storageUsagePercentage}%` }}
                        />
                      </div>
                      <span className="text-sm text-gray-700">{resource?.storageUsagePercentage}%</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">{(resource?.storageUsageMb / 1024)?.toFixed(2)} GB</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {resource?.activeConnections}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{resource?.apiRequestsCount}</div>
                    <div className="text-xs text-gray-500">{resource?.databaseQueriesCount} DB queries</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${getAlertColor(resource?.alertLevel)}`}>
                      {resource?.alertLevel}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(resource?.recordedAt)?.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ClientResourcePanel;